import { Undo2, Check, GitCompareArrows } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { DiffCard } from '@/features/companion/edit-diff-store'

interface EditDiffCardProps {
  card: DiffCard
  onKeep: (batchId: string) => void
  onUndo: (batchId: string) => void
}

/**
 * Inline chat card describing one AI graph-edit batch. Pending cards show
 * Keep / Undo; kept cards collapse to a compact record; undone cards strike
 * through. Un-actioned cards stay "pending" — the graph is already live, so
 * the card is a review affordance, not a gate.
 */
export function EditDiffCard({ card, onKeep, onUndo }: EditDiffCardProps) {
  const isUndone = card.status === 'undone'
  const isKept = card.status === 'kept'
  const isPending = card.status === 'pending'

  return (
    <div className="mx-3 my-1.5">
      <div
        className={cn(
          'rounded-lg border bg-muted/40 px-3 py-2',
          isUndone ? 'border-border/40 opacity-60' : 'border-border',
        )}
      >
        <div className="flex items-center gap-1.5">
          <GitCompareArrows size={12} className="shrink-0 text-primary" />
          <span className={cn('text-xs font-medium text-foreground', isUndone && 'line-through')}>
            {card.title}
          </span>
          {isKept && <span className="ml-auto text-[10px] text-muted-foreground">Kept</span>}
          {isUndone && <span className="ml-auto text-[10px] text-muted-foreground">Undone</span>}
        </div>

        <ul className={cn('mt-1.5 space-y-0.5', isUndone && 'line-through')}>
          {card.diffLines.map((line, i) => (
            <li key={i} className="font-mono text-[11px] leading-snug text-muted-foreground">
              {line.text}
            </li>
          ))}
        </ul>

        {isPending && (
          <div className="mt-2 flex items-center justify-end gap-1">
            <button
              onClick={() => onUndo(card.batchId)}
              className="flex items-center gap-1 rounded px-2 py-1 text-[11px] text-destructive hover:bg-destructive/10"
            >
              <Undo2 size={11} />
              Undo
            </button>
            <button
              onClick={() => onKeep(card.batchId)}
              className="flex items-center gap-1 rounded px-2 py-1 text-[11px] text-primary hover:bg-primary/10"
            >
              <Check size={11} />
              Keep
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
